import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Tooltip,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import QuizIcon from '@mui/icons-material/Quiz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useAuth } from '../../contexts/AuthContext';
import documentService, { Document as DocumentType } from '../../services/documentService';
import { renderFileTypeIcon, formatDate } from '../../config/fileUtils';

const DocumentList: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentType | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [processingDocuments, setProcessingDocuments] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const { canCreateQuizzes } = useAuth();

  // Fetch documents on component mount
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Fetch documents from API
  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await documentService.getDocuments();
      setDocuments(response.documents);
    } catch (err: any) {
      setError(err.message || 'Không thể tải tài liệu');
    } finally {
      setLoading(false);
    }
  };

  // Handle document view
  // Handle document click to navigate to detail page
  const handleDocumentClick = (document: DocumentType) => {
    navigate(`/documents/${document.id}`);
  };



  // Handle delete dialog open
  const handleDeleteDialogOpen = (document: DocumentType) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  // Handle document delete
  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    setDeleteLoading(true);
    try {
      await documentService.deleteDocument(documentToDelete.id);
      setDocuments(documents.filter(doc => doc.id !== documentToDelete.id));
      handleDeleteDialogClose();
    } catch (err: any) {
      setError(err.message || 'Không xóa được tài liệu');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Navigate to upload page
  const handleUploadClick = () => {
    navigate('/documents/upload');
  };

  // Handle document processing
  const handleProcessDocument = async (document: DocumentType) => {
    if (processingDocuments.has(document.id)) return;

    setProcessingDocuments(prev => new Set(prev).add(document.id));
    setError(null);

    try {
      // Start processing
      await documentService.processDocument(document.id, 10);
      
      // Update document status in the list
      setDocuments(prev => prev.map(doc => 
        doc.id === document.id 
          ? { ...doc, processing_status: 'processing' }
          : doc
      ));

      // Use the new polling method
      try {
        const statusResponse = await documentService.pollProcessingStatus(document.id);
        
        if (statusResponse.success) {
          const newStatus = statusResponse.status_details?.status || statusResponse.processing_status;
          
          setDocuments(prev => prev.map(doc => 
            doc.id === document.id 
              ? { 
                  ...doc, 
                  processing_status: newStatus,
                  processed: newStatus === 'completed'
                }
              : doc
          ));
        }
      } catch (pollError: any) {
        console.error('Polling error:', pollError);
        if (pollError.message.includes('timeout')) {
          setError('Hết thời gian xử lý - vui lòng kiểm tra trạng thái theo cách thủ công');
        }
      }

    } catch (err: any) {
      setError(err.message || 'Không xử lý được tài liệu');
    } finally {
      setProcessingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(document.id);
        return newSet;
      });
    }
  };



  // Render status chip
  const renderStatusChip = (document: DocumentType) => {
    const isProcessing = processingDocuments.has(document.id);
    
    if (isProcessing) {
      return (
        <Chip 
          label="Đang xử lý..." 
          color="warning" 
          size="small"
          icon={<CircularProgress size={16} />}
        />
      );
    }
    
    if (document.processed) {
      return <Chip label="Hoàn thành" color="success" size="small" />;
    }
    
    if (document.processing_status === 'processing_failed' || document.processing_status === 'upload_failed' || document.processing_status === 'failed') {
      const errorMessage = document.error_message || 'Xử lý không thành công';
      return (
        <Tooltip title={errorMessage} arrow>
          <Chip label="Thất bại" color="error" size="small" />
        </Tooltip>
      );
    }
    
    if (document.processing_status === 'processing' || document.processing_status === 'pending_processing') {
      return <Chip label="Đang xử lý" color="warning" size="small" />;
    }
    
    return <Chip label="Chưa giải quyết" color="default" size="small" />;
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Tài liệu của tôi
          </Typography>
          {canCreateQuizzes() && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleUploadClick}
            >
              Tải lên tài liệu
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Không tìm thấy tài liệu nào. Tải lên tài liệu đầu tiên của bạn để bắt đầu.
            </Typography>
            {canCreateQuizzes() && (
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<AddIcon />}
                onClick={handleUploadClick}
                sx={{ mt: 2 }}
              >
                Tải lên tài liệu
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Loại</TableCell>
                  <TableCell>Tên</TableCell>
                  <TableCell>Thời gian tải lên</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell align="right">Hành động</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow 
                    key={document.id} 
                    hover 
                    onClick={() => handleDocumentClick(document)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      {renderFileTypeIcon(document.file_type)}
                    </TableCell>
                    <TableCell>{document.name}</TableCell>
                    <TableCell>{formatDate(document.created_at)}</TableCell>
                    <TableCell sx={{pointerEvents: 'none'}}>
                      {renderStatusChip(document)}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {!document.processed && 
                       document.processing_status !== 'processing' && 
                       document.processing_status !== 'pending_processing' &&
                       document.processing_status !== 'processing_failed' &&
                       document.processing_status !== 'upload_failed' && (
                        <Tooltip title="Xử lý lại tài liệu">
                          <IconButton 
                            color="primary" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProcessDocument(document);
                            }}
                            disabled={processingDocuments.has(document.id)}
                          >
                            {processingDocuments.has(document.id) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayArrowIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {document.processed && (
                        <Tooltip title="Xem câu hỏi">
                          <IconButton 
                            color="success" 
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/documents/${document.id}`);
                            }}
                          >
                            <QuizIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Xóa">
                        <IconButton 
                          color="error" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDialogOpen(document);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn xóa tài liệu "{documentToDelete?.name}"? Thao tác này không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteDocument} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DocumentList; 